import React, { useEffect, useMemo, useRef, useState } from "react"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import IconButton from "@mui/material/IconButton"
import IconsColor from "../../../util/iconsColor"
import {
  QuotationHotelOptionsByDateHotel,
  QuotationHotelOptionsByDateRoomType,
  QuotationHotelOptionsPricingOption,
} from "../../../models/axiosModel"
import styles from "./HotelRoomSelectorV2.module.scss"
import Toolbar from "./components/Toolbar"
import HotelListItem from "./components/HotelListItem"
import RoomCard from "./components/RoomCard"
import { HotelRoomSelectorV2Props, SelectedRoomState } from "./types"
import { toTitleCase } from "util/textcase"

const resolveMealCode = (name?: string | null): string => {
  const v = String(name || "").toUpperCase()
  if (v.includes("AI")) return "AI"
  if (v.includes("HB")) return "HB"
  if (v.includes("BB")) return "BB"
  return ""
}

const pickPricingOption = (
  rt: QuotationHotelOptionsByDateRoomType,
  roomsNeeded: number,
  selectedMealPlanId?: number,
): QuotationHotelOptionsPricingOption | undefined => {
  const opts = rt.pricing_options || []
  if (!opts.length) return undefined
  if (opts[0].pricing_mode === "meal") {
    if (selectedMealPlanId != null) {
      return opts.find((o) => Number(o.meal_plan_type_id) === Number(selectedMealPlanId)) || opts[0]
    }
    return opts[0]
  }
  const gitMinRooms = opts[0].git_min_rooms
  if (gitMinRooms == null) return opts[0]
  if (roomsNeeded >= gitMinRooms) {
    return opts.find((o) => Number(o.git_fit_type_id) === 1) || opts[0]
  }
  return opts.find((o) => Number(o.git_fit_type_id) === 2) || opts[0]
}

const collectMealPlans = (hotel: QuotationHotelOptionsByDateHotel): Array<{ id: number; name: string }> => {
  const map = new Map<number, string>()
  ;(hotel.hotel_room_types || []).forEach((rt) => {
    ;(rt.pricing_options || []).forEach((po) => {
      if (po.pricing_mode === "meal" && po.meal_plan_type_id != null) {
        map.set(Number(po.meal_plan_type_id), String(po.meal_plan_type_name || ""))
      }
    })
  })
  return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
}

const HotelRoomSelectorV2: React.FC<HotelRoomSelectorV2Props> = ({
  open,
  onClose,
  cityName,
  hotels,
  roomsNeeded,
  selectedHotelId,
  selectedRoomTypeId,
  selectedMealPlanTypeId,
  selectedMealPlanTypeName,
  onSelect,
}) => {
  const [search, setSearch] = useState("")
  const [starFilter, setStarFilter] = useState("all")
  const [sort, setSort] = useState("default")
  const [activeHotelId, setActiveHotelId] = useState("")
  const [mealPlanByHotel, setMealPlanByHotel] = useState<Record<string, number>>({})
  const mealPlanByHotelRef = useRef<Record<string, number>>({})
  const [selected, setSelected] = useState<SelectedRoomState | null>(null)

  useEffect(() => {
    if (!open) return
    setSearch("")
    setStarFilter("all")
    setSort("default")
    const nextMealPlan: Record<string, number> = {}
    hotels.forEach((h) => {
      const plans = collectMealPlans(h)
      if (plans.length > 0) {
        let flagReturn = false;
        plans.forEach((plan) => {
          if(flagReturn) return;
          if(h.hotel_id === selectedHotelId) {
            nextMealPlan[h.hotel_id] = plan.id
            if (selectedMealPlanTypeId != null) {
              const exact = plans.find((p) => Number(p.id) === Number(selectedMealPlanTypeId))
              if (exact) {
                nextMealPlan[h.hotel_id] = exact.id
                flagReturn = true;
                return
              }
            }
            if (selectedMealPlanTypeName) {
              const wantedCode = resolveMealCode(selectedMealPlanTypeName)
              const match = plans.find((p) => resolveMealCode(p.name) === wantedCode)
              if (match) nextMealPlan[h.hotel_id] = match.id
            }
          }
        })
      }
    })
    setMealPlanByHotel(nextMealPlan)
    mealPlanByHotelRef.current = nextMealPlan
    setActiveHotelId(selectedHotelId || hotels[0]?.hotel_id || "")
    if (selectedHotelId && selectedRoomTypeId) {
      const hotel = hotels.find((h) => h.hotel_id === selectedHotelId)
      const rt = (hotel?.hotel_room_types || []).find((r) => r.hotel_room_type_id === selectedRoomTypeId)
      if (hotel && rt) {
        const po = pickPricingOption(rt, roomsNeeded, nextMealPlan[hotel.hotel_id])
        setSelected({
          hotelId: hotel.hotel_id,
          roomId: rt.hotel_room_type_id,
          price: Number(po?.room_price || 0),
          pricingOption: po,
        })
      } else {
        setSelected(null)
      }
    } else {
      setSelected(null)
    }
  }, [open, hotels, roomsNeeded, selectedHotelId, selectedRoomTypeId, selectedMealPlanTypeId, selectedMealPlanTypeName])

  const filteredHotels = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (hotels || []).filter((h) => {
      if (starFilter !== "all" && Number(h.rating) !== Number(starFilter)) return false
      if (!q) return true
      const inName = String(h.hotel_name || "").toLowerCase().includes(q)
      const inRoom = (h.hotel_room_types || []).some((rt) => String(rt.hotel_room_type_name || "").toLowerCase().includes(q))
      return inName || inRoom
    })
  }, [hotels, starFilter, search])

  const activeHotel = useMemo(
    () => filteredHotels.find((h) => h.hotel_id === activeHotelId) || filteredHotels[0],
    [filteredHotels, activeHotelId],
  )

  const visibleRooms = useMemo(() => {
    const q = search.trim().toLowerCase()
    const base = (activeHotel?.hotel_room_types || []).filter((rt) =>
      !q || String(rt.hotel_room_type_name || "").toLowerCase().includes(q) || String(activeHotel?.hotel_name || "").toLowerCase().includes(q),
    )
    const rooms = base.map((rt) => ({
      roomType: rt,
      pricingOption: pickPricingOption(rt, roomsNeeded, activeHotel ? mealPlanByHotel[activeHotel.hotel_id] : undefined),
      price: Number(pickPricingOption(rt, roomsNeeded, activeHotel ? mealPlanByHotel[activeHotel.hotel_id] : undefined)?.room_price || 0),
    }))
    if (sort === "price_asc") return [...rooms].sort((a, b) => a.price - b.price)
    if (sort === "price_desc") return [...rooms].sort((a, b) => b.price - a.price)
    return rooms
  }, [activeHotel, mealPlanByHotel, roomsNeeded, search, sort])

  const fromPriceForHotel = (hotel: QuotationHotelOptionsByDateHotel): number => {
    const selectedMealId = mealPlanByHotel[hotel.hotel_id]
    const values = (hotel.hotel_room_types || []).map((rt) => Number(pickPricingOption(rt, roomsNeeded, selectedMealId)?.room_price || 0))
    return values.length ? Math.min(...values) : 0
  }

  const firstRt = activeHotel?.hotel_room_types?.[0]
  const firstPo = firstRt ? pickPricingOption(firstRt, roomsNeeded, activeHotel ? mealPlanByHotel[activeHotel.hotel_id] : undefined) : undefined
  const activeMealPlans = activeHotel ? collectMealPlans(activeHotel) : []
  const activeMealPlanId = activeHotel ? mealPlanByHotel[activeHotel.hotel_id] : undefined

  const handleMealPlanChange = (hotel: QuotationHotelOptionsByDateHotel, nextMealPlanId: number) => {
    mealPlanByHotelRef.current = {
      ...mealPlanByHotelRef.current,
      [hotel.hotel_id]: nextMealPlanId,
    }
    setMealPlanByHotel((prev) => ({
      ...prev,
      [hotel.hotel_id]: nextMealPlanId,
    }))

    // Preserve selected room context and recalculate its pricing for the new meal plan.
    setSelected((prev) => {
      if (!prev || prev.hotelId !== hotel.hotel_id) return prev
      const selectedRt = (hotel.hotel_room_types || []).find((rt) => rt.hotel_room_type_id === prev.roomId)
      if (!selectedRt) return prev
      const selectedPo = pickPricingOption(selectedRt, roomsNeeded, nextMealPlanId)
      return {
        ...prev,
        pricingOption: selectedPo,
        price: Number(selectedPo?.room_price || 0),
      }
    })

  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={true}
      maxWidth={false}
      aria-labelledby="hotel-selector-v2-title"
      PaperProps={{ className: styles.dialogPaper }}
    >
      <DialogTitle className={styles.header} id="hotel-selector-v2-title">
        <div className={styles.title}>
          Select Hotel & Room Type
        </div>
        <IconButton aria-label="Close dialog" className={styles.closeBtn} onClick={onClose}>
          <IconsColor iconName="closeIcon" width="18px" height="18px" fillColor="none" strokeColor="currentColor" />
        </IconButton>
      </DialogTitle>
      <Toolbar
        search={search}
        setSearch={setSearch}
        starFilter={starFilter}
        setStarFilter={setStarFilter}
        sort={sort}
        setSort={setSort}
      />
      <DialogContent style={{ padding: 0 }}>
        <div className={styles.body}>
          <div className={styles.leftPane}>
            {filteredHotels.length === 0 ? (
              <div className={styles.emptyBox}>No hotels match your search</div>
            ) : (
              filteredHotels.map((hotel) => (
                <HotelListItem
                  key={hotel.hotel_id}
                  hotel={hotel}
                  active={hotel.hotel_id === activeHotel?.hotel_id}
                  selected={hotel.hotel_id === selectedHotelId}
                  fromPrice={fromPriceForHotel(hotel)}
                  onClick={() => setActiveHotelId(hotel.hotel_id)}
                />
              ))
            )}
          </div>
          <div className={styles.rightPane}>
            {!activeHotel ? (
              <div className={styles.emptyBox}>No room types available</div>
            ) : (
              <>
                <div className={styles.rightHeader}>
                  <div>
                    <h3 className={styles.rightTitle}>{toTitleCase(activeHotel.hotel_name)}</h3>
                    <div className={styles.rightMeta}>
                      <span className={styles.stars}>{"★".repeat(Math.max(0, Math.min(5, Number(activeHotel.rating || 0))))}</span>
                      <span>· {toTitleCase(cityName || activeHotel.city_name)}</span>
                      {activeMealPlanId != null && (
                        <span>
                          · Meal Plan: {toTitleCase(activeMealPlans.find((p) => Number(p.id) === Number(activeMealPlanId))?.name || selectedMealPlanTypeName || "-")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={styles.chips}>
                    {activeMealPlans.length > 0 && (
                      <span className={styles.chip}>
                        Meal Plan:
                        <select
                          className={styles.mealPlanSelect}
                          value={String(activeMealPlanId ?? activeMealPlans[0]?.id ?? "")}
                          onChange={(e) => handleMealPlanChange(activeHotel, Number(e.target.value))}
                        >
                          {activeMealPlans.map((plan) => (
                            <option key={plan.id} value={plan.id}>
                              {toTitleCase(plan.name)}
                            </option>
                          ))}
                        </select>
                      </span>
                    )}
                    <span className={styles.chip}>Child With Bed <b>VND {Number(firstPo?.child_with_bed_price || 0).toLocaleString("en-US")}</b></span>
                    <span className={styles.chip}>Child without Bed <b>VND {Number(firstPo?.child_without_bed_price || 0).toLocaleString("en-US")}</b></span>
                  </div>
                </div>
                <div className={styles.roomGrid}>
                  {visibleRooms.map((r) => (
                    <RoomCard
                      key={r.roomType.hotel_room_type_id}
                      name={toTitleCase(r.roomType.hotel_room_type_name)}
                      price={r.price}
                      selected={selected?.hotelId === activeHotel.hotel_id && selected?.roomId === r.roomType.hotel_room_type_id}
                      onClick={() => {
                        const selectedMealPlanId = mealPlanByHotelRef.current[activeHotel.hotel_id]
                        const selectedPricingOption = pickPricingOption(
                          r.roomType,
                          roomsNeeded,
                          selectedMealPlanId,
                        )
                        const resolvedPrice = Number(selectedPricingOption?.room_price || 0)
                        const nextSelection: SelectedRoomState = {
                          hotelId: activeHotel.hotel_id,
                          roomId: r.roomType.hotel_room_type_id,
                          price: resolvedPrice,
                          pricingOption: selectedPricingOption,
                        }
                        setSelected(nextSelection)
                        onSelect(activeHotel, r.roomType, selectedPricingOption, resolvedPrice)
                        onClose()
                      }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default HotelRoomSelectorV2
